import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  friendsApi,
  Friend,
  FriendRequest,
  FriendGroup,
  FriendSuggestion,
} from "../services/friendsApi";
import { useAuth } from "./AuthProvider";
import { useNotification } from "./NotificationContext";

// Type guard and helper for extracting error messages from axios-like errors
interface AxiosLikeError {
  response?: {
    data?: {
      error?: string;
    };
  };
  message?: string;
}

function getErrorMessage(error: unknown, defaultMessage: string): string {
  const err = error as AxiosLikeError;
  return err?.response?.data?.error || err?.message || defaultMessage;
}

interface FriendsContextType {
  // State
  friends: Friend[];
  friendRequests: FriendRequest[];
  sentRequests: FriendRequest[];
  friendGroups: FriendGroup[];
  suggestions: FriendSuggestion[];
  blockedUsers: Friend[];

  // Loading states
  isLoading: boolean;
  isRequestsLoading: boolean;
  isSuggestionsLoading: boolean;

  // Counts
  friendCount: number;
  pendingRequestCount: number;

  // Actions
  loadFriends: () => Promise<void>;
  loadGroups: () => Promise<void>;
  loadFriendRequests: () => Promise<void>;
  loadSuggestions: () => Promise<void>;
  sendFriendRequest: (recipientId: number, message?: string) => Promise<void>;
  acceptFriendRequest: (requestId: number) => Promise<void>;
  declineFriendRequest: (requestId: number) => Promise<void>;
  removeFriend: (friendId: number) => Promise<void>;
  blockUser: (userId: number, reason?: string) => Promise<void>;
  unblockUser: (userId: number) => Promise<void>;
  createGroup: (
    name: string,
    description?: string,
    color?: string,
    friendIds?: (string | number)[],
  ) => Promise<void>;
  updateGroup: (
    groupId: number,
    name: string,
    description?: string,
    color?: string,
  ) => Promise<void>;
  deleteGroup: (groupId: number) => Promise<void>;
  // Returns a variable shape from the backend (can be an array, {members: Friend[]},
  // or an envelope with {data: {...}}). Consumers inspect at runtime.
  getGroupMembers: (groupId: number) => Promise<unknown>;
  addFriendToGroup: (friendId: number, groupId: number) => Promise<void>;
  removeFromGroup: (friendId: number, groupId: number) => Promise<void>;
  searchUsers: (query: string) => Promise<FriendSuggestion[]>;
}

const FriendsContext = createContext<FriendsContextType | undefined>(undefined);

export const useFriends = () => {
  const context = useContext(FriendsContext);
  if (!context) {
    throw new Error("useFriends must be used within a FriendsProvider");
  }
  return context;
};

interface FriendsProviderProps {
  children: ReactNode;
}

export const FriendsProvider: React.FC<FriendsProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotification();

  // State
  const [friends, setFriends] = useState<Friend[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [friendGroups, setFriendGroups] = useState<FriendGroup[]>([]);
  const [suggestions, setSuggestions] = useState<FriendSuggestion[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<Friend[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestsLoading, setIsRequestsLoading] = useState(false);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

  // Computed values
  const friendCount = friends.filter((f) => f.status === "accepted").length;
  const pendingRequestCount = friendRequests.filter(
    (r) => r.status === "pending",
  ).length;

  // Load friends
  const loadFriends = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const [acceptedResponse, blockedResponse] = await Promise.all([
        friendsApi.getFriends("accepted"),
        friendsApi.getFriends("blocked"),
      ]);

      setFriends(acceptedResponse.data.friends);
      // Don't overwrite groups here - they are managed by loadGroups()
      setBlockedUsers(blockedResponse.data.friends);
    } catch (error) {
      console.error("Failed to load friends:", error);
      addNotification("Failed to load friends", "error");
    } finally {
      setIsLoading(false);
    }
  }, [user, addNotification]);

  // Load groups (dedicated function)
  const loadGroups = useCallback(async () => {
    if (!user) return;

    try {
      const response = await friendsApi.getFriendGroups();
      setFriendGroups(response.data.groups || []);
    } catch (error) {
      console.error("Failed to load groups:", error);
      addNotification("Failed to load groups", "error");
    }
  }, [user, addNotification]);

  // Load friend requests
  const loadFriendRequests = useCallback(async () => {
    if (!user) return;

    setIsRequestsLoading(true);
    try {
      const [receivedResponse, sentResponse] = await Promise.all([
        friendsApi.getFriendRequests("received"),
        friendsApi.getFriendRequests("sent"),
      ]);

      setFriendRequests(receivedResponse.data.requests);
      setSentRequests(sentResponse.data.requests);
    } catch (error) {
      console.error("Failed to load friend requests:", error);
      addNotification("Failed to load friend requests", "error");
    } finally {
      setIsRequestsLoading(false);
    }
  }, [user, addNotification]);

  // Load suggestions
  const loadSuggestions = useCallback(async () => {
    if (!user) return;

    setIsSuggestionsLoading(true);
    try {
      const response = await friendsApi.getFriendSuggestions(10);
      setSuggestions(response.data.suggestions);
    } catch (error) {
      console.error("Failed to load suggestions:", error);
    } finally {
      setIsSuggestionsLoading(false);
    }
  }, [user]);

  // Send friend request
  const sendFriendRequest = useCallback(
    async (recipientId: number, message?: string) => {
      try {
        await friendsApi.sendFriendRequest(recipientId, message);
        addNotification("Friend request sent!", "success");
        await loadFriendRequests();

        // Remove from suggestions if present
        setSuggestions((prev) => prev.filter((s) => s.id !== recipientId));
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          "Failed to send friend request",
        );
        addNotification(errorMessage, "error");
        throw error;
      }
    },
    [addNotification, loadFriendRequests],
  );

  // Accept friend request
  const acceptFriendRequest = useCallback(
    async (requestId: number) => {
      try {
        await friendsApi.respondToFriendRequest(requestId, "accept");
        addNotification("Friend request accepted!", "success");

        // Reload friends and requests
        await Promise.all([loadFriends(), loadFriendRequests()]);
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          "Failed to accept friend request",
        );
        addNotification(errorMessage, "error");
      }
    },
    [addNotification, loadFriends, loadFriendRequests],
  );

  // Decline friend request
  const declineFriendRequest = useCallback(
    async (requestId: number) => {
      try {
        await friendsApi.respondToFriendRequest(requestId, "decline");
        addNotification("Friend request declined", "info");
        await loadFriendRequests();
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          "Failed to decline friend request",
        );
        addNotification(errorMessage, "error");
      }
    },
    [addNotification, loadFriendRequests],
  );

  // Remove friend
  const removeFriend = useCallback(
    async (friendId: number) => {
      try {
        await friendsApi.removeFriend(friendId);
        addNotification("Friend removed", "info");
        await loadFriends();
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error, "Failed to remove friend");
        addNotification(errorMessage, "error");
      }
    },
    [addNotification, loadFriends],
  );

  // Block user
  const blockUser = useCallback(
    async (userId: number, reason?: string) => {
      try {
        await friendsApi.blockUser(userId, reason);
        addNotification("User blocked", "success");
        await loadFriends();
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error, "Failed to block user");
        addNotification(errorMessage, "error");
      }
    },
    [addNotification, loadFriends],
  );

  // Unblock user
  const unblockUser = useCallback(
    async (userId: number) => {
      try {
        await friendsApi.unblockUser(userId);
        addNotification("User unblocked", "success");
        await loadFriends();
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error, "Failed to unblock user");
        addNotification(errorMessage, "error");
      }
    },
    [addNotification, loadFriends],
  );

  // Create group
  const createGroup = useCallback(
    async (
      name: string,
      description?: string,
      color?: string,
      friendIds?: (string | number)[],
    ) => {
      try {
        const response = await friendsApi.createFriendGroup(
          name,
          description,
          color,
          friendIds,
        );
        console.log("Create group response:", response);
        addNotification("Friend group created!", "success");
        await loadGroups(); // Use dedicated groups loader
        return response.data?.group || response; // Consistent return
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error, "Failed to create group");
        addNotification(errorMessage, "error");
        throw error;
      }
    },
    [addNotification, loadGroups],
  );

  // Update group
  const updateGroup = useCallback(
    async (
      groupId: number,
      name: string,
      description?: string,
      color?: string,
    ) => {
      try {
        const response = await friendsApi.updateFriendGroup(
          groupId,
          name,
          description,
          color,
        );
        console.log("Update group response:", response);
        addNotification("Friend group updated!", "success");
        await loadGroups(); // Use dedicated groups loader
        return response.data?.group || response; // Consistent return
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error, "Failed to update group");
        addNotification(errorMessage, "error");
        throw error;
      }
    },
    [addNotification, loadGroups],
  );

  // Delete group
  const deleteGroup = useCallback(
    async (groupId: number) => {
      try {
        await friendsApi.deleteFriendGroup(groupId);
        addNotification("Friend group deleted!", "success");
        await loadGroups(); // Use dedicated groups loader
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error, "Failed to delete group");
        addNotification(errorMessage, "error");
      }
    },
    [addNotification, loadGroups],
  );

  // Get group members
  const getGroupMembers = useCallback(
    async (groupId: number) => {
      try {
        const response = await friendsApi.getGroupMembers(groupId);
        return response.data;
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          "Failed to get group members",
        );
        addNotification(errorMessage, "error");
        throw error;
      }
    },
    [addNotification],
  );

  // Remove from group
  const removeFromGroup = useCallback(
    async (friendId: number, groupId: number) => {
      try {
        await friendsApi.removeFromGroup(friendId, groupId);
        addNotification("Friend removed from group!", "success");
        await loadGroups(); // Use dedicated groups loader to update counts
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          "Failed to remove friend from group",
        );
        addNotification(errorMessage, "error");
      }
    },
    [addNotification, loadGroups],
  );

  // Add friend to group
  const addFriendToGroup = useCallback(
    async (friendId: number, groupId: number) => {
      try {
        await friendsApi.addToGroup(friendId, groupId);
        addNotification("Friend added to group!", "success");
        await loadGroups(); // Use dedicated groups loader to update counts
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          "Failed to add friend to group",
        );
        addNotification(errorMessage, "error");
      }
    },
    [addNotification, loadGroups],
  );

  // Search users
  const searchUsers = useCallback(async (query: string) => {
    try {
      const response = await friendsApi.searchUsers(query);
      return response.data.users || [];
    } catch (error) {
      console.error("Failed to search users:", error);
      return [];
    }
  }, []);

  // Load initial data when user logs in
  useEffect(() => {
    if (user) {
      loadFriends();
      loadGroups(); // Load groups separately
      loadFriendRequests();
      loadSuggestions();
    } else {
      // Clear data when user logs out
      setFriends([]);
      setFriendRequests([]);
      setSentRequests([]);
      setFriendGroups([]);
      setSuggestions([]);
      setBlockedUsers([]);
    }
  }, [user, loadFriends, loadGroups, loadFriendRequests, loadSuggestions]);

  const value: FriendsContextType = {
    friends,
    friendRequests,
    sentRequests,
    friendGroups,
    suggestions,
    blockedUsers,
    isLoading,
    isRequestsLoading,
    isSuggestionsLoading,
    friendCount,
    pendingRequestCount,
    loadFriends,
    loadGroups,
    loadFriendRequests,
    loadSuggestions,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    removeFriend,
    blockUser,
    unblockUser,
    createGroup,
    updateGroup,
    deleteGroup,
    getGroupMembers,
    addFriendToGroup,
    removeFromGroup,
    searchUsers,
  };

  return (
    <FriendsContext.Provider value={value}>{children}</FriendsContext.Provider>
  );
};
